import * as React from 'react';
import { EventEmitter } from 'events';
import {
  CommandingSurfaceGroup,
  CommandingSurfaceViewProvider,
  CommandingSurfaceViewParams,
  CommandProps,
  ThemeProperties,
  StylingProvider
} from '@fluidx/office-fluid-types';
import { CommandingSurface } from './CommandingSurface';
import { ITelemetryBaseLogger } from '@fluidframework/common-definitions';

/** Notification for when display is called */
const CallDisplayEventName = 'callDisplay';

/** Notification for when dismiss is called */
const CallDismissEventName = 'callDismiss';

/**
 * Implementation of CommandingSurfaceViewProvider which simply exposes the
 * CommandingSurfaceViewParams it is given to whatever UI is listening.
 */
class CommandingSurfaceViewAdapter extends EventEmitter implements CommandingSurfaceViewProvider {
  /** The current set of display params */
  public currentDisplayParams?: CommandingSurfaceViewParams;

  public get CommandingSurfaceViewProvider() {
    return this;
  }

  public display(params: CommandingSurfaceViewParams): void {
    this.currentDisplayParams = params;
    this.emit(CallDisplayEventName);
  }

  public dismiss(): void {
    this.currentDisplayParams = undefined;
    this.emit(CallDismissEventName);
  }

  public readonly usesCoordinates = true;
}

let currentTarget: HTMLElement | undefined;

const removeTarget = () => {
  if (currentTarget !== undefined) {
    currentTarget.remove();
  }
};

/**
 * Creates an absolutely positioned div at coordinates (x,y) for the commanding surface
 * menu to be placed in.
 */
const createContainerElement = (x: number, y: number, fluidHostingElementId: string): HTMLElement => {
  const containerElement = document.createElement('div');

  // Position the div where the menu was invoked.
  containerElement.style.position = 'absolute';

  const fluidHostingElement = document.getElementById(fluidHostingElementId);

  if (fluidHostingElement) {
    const divStyles = window.getComputedStyle(fluidHostingElement);
    const paddingLeft = parseFloat(divStyles.getPropertyValue('padding-left'));
    const paddingTop = parseFloat(divStyles.getPropertyValue('padding-top'));
    const elementRect = fluidHostingElement.getBoundingClientRect();

    containerElement.style.left = elementRect.left + window.scrollX + paddingLeft + x + 'px';
    containerElement.style.top = elementRect.top + window.scrollY + paddingTop + y + 'px';
  } else {
    containerElement.style.left = x + 'px';
    containerElement.style.top = y + 'px';
  }

  document.body.appendChild(containerElement);

  return containerElement;
};

interface CommandingSurfaceUXProps {
  /**
   * Implementation of CommandingSurfaceViewProvider that is returned by getCommandingSurfaceParts function.
   * Allows Fluid to send commands through the iframe boundary.
   * Should be the same CommandingSurfaceViewProvider set in the scope.
   */
  viewProvider?: CommandingSurfaceViewAdapter;

  /**
   * Id of the element that Fluid is hosted within.
   * To calculate the position of the Commanding Surface
   */
  fluidHostingElementId?: string;
}

/**
 * All parts required to display the Commanding Surface outside of the sourceless iframe.
 */
export interface CommandingSurfaceParts {
  /**
   * Implementation of CommandingSurfaceViewProvider
   * to be passed into Fluid scope and commandingSurfaceUX
   */
  viewProvider: CommandingSurfaceViewAdapter;

  /** UX to be used by a host to render the Commanding Surface */
  commandingSurfaceUX: (props: CommandingSurfaceUXProps) => JSX.Element | null;
}

/**
 * Gets all parts required to display the Commanding Surface outside of the sourceless iframe.
 * Arguments given to this function should match those added to the Fluid scope for consistency.
 *
 * @param logger to log events from within the Commanding Surface
 * @param stylingProvider to add theming to CommandingSurface
 * @param locale to dictate language and UI direction
 */
export function getCommandingSurfaceParts(
  logger: ITelemetryBaseLogger | undefined,
  stylingProvider: StylingProvider | undefined,
  locale: string | undefined
): CommandingSurfaceParts {
  const viewProvider = new CommandingSurfaceViewAdapter();

  const cssProperties = () => {
    return getCssProperties(stylingProvider?.themeProperties);
  };

  const commandingSurfaceUX = (props: CommandingSurfaceUXProps) => {
    const { viewProvider, fluidHostingElementId } = props;

    if (!viewProvider) {
      return null;
    }
    const [displayParams, setDisplayParams] = React.useState(viewProvider.currentDisplayParams);

    React.useEffect(() => {
      if (viewProvider) {
        viewProvider.on(CallDisplayEventName, () => {
          setDisplayParams(viewProvider.currentDisplayParams);
        });

        viewProvider.on(CallDismissEventName, () => {
          removeTarget();
          setDisplayParams(undefined);
        });
      }

      return () => {
        if (viewProvider) {
          viewProvider.removeAllListeners();
        }
      };
    }, [viewProvider]);

    if (!displayParams) {
      return null;
    }

    // Clear the current visuals if there are any, to ensure we only have one commanding
    // surface open at once.
    removeTarget();
    currentTarget = createContainerElement(
      displayParams.coordinates?.clientX || 0,
      displayParams.coordinates?.clientY || 0,
      fluidHostingElementId || ''
    );

    const topBarCommands = getTransformedCommands(displayParams.topBarCommands);
    const mainBodyCommands = getTransformedCommands(displayParams.mainBodyCommands);

    const onDismiss = () => {
      viewProvider.dismiss();
      if (displayParams?.previousElement) {
        displayParams.previousElement.focus();
      }
    };

    return (
      <CommandingSurface
        target={currentTarget}
        topBarCommands={topBarCommands}
        commandGroups={mainBodyCommands}
        miniMode={displayParams.mainBodyInitiallyHidden}
        disableInitialFocus={!displayParams.focusMode}
        triggerByKeyboardEvent={displayParams.focusMode === 'focus'}
        onDismiss={onDismiss}
        logger={logger}
        cssProperties={cssProperties}
        locale={locale}
      />
    );
  };

  return { viewProvider, commandingSurfaceUX };
}

function getCssProperties(themeProperties: ThemeProperties): { [propertyName: string]: string } | undefined {
  let cssProperties: { [propertyName: string]: string } | undefined;

  themeProperties?.forEach((colorValue, themePropertyName) => {
    if (cssProperties === undefined) {
      cssProperties = {};
    }
    cssProperties[themePropertyName] = colorValue;
  });

  return cssProperties;
}

/**
 * Transform the CommandingSurfaceGroup[] in CommandingSurfaceViewProvider into CommandProps[][]
 */
function getTransformedCommands(commands?: CommandingSurfaceGroup[]): CommandProps[][] | undefined {
  if (!commands) {
    return undefined;
  }

  let transformedCommands: CommandProps[][] = [];

  commands.forEach((group) => {
    transformedCommands.push(group.commands);
  });

  return transformedCommands;
}
